// Global (cross-company) access Schedule client — SPA client for
// /api/schedules and its /spans sub-resource, plus the company:schedule
// assignment endpoints on /api/companies/:id/schedules. Every function takes
// the Auth0 access `token` explicitly, threaded last to match other API
// client call sites. Schedules are the source of truth for access windows —
// see docs/plans/2026-08-09-global-access-schedules.md.
import { apiRequest } from './client'
import type { Schedule, ScheduleSpan } from '@hub-wifi/shared'

export type { Schedule, ScheduleSpan }

/** Weekly access-window payload shape accepted by PUT /spans (no id/scheduleId/createdAt — those are server-assigned). */
export interface ScheduleSpanInput {
  startMinute: number
  endMinute: number
  sun: boolean
  mon: boolean
  tue: boolean
  wed: boolean
  thu: boolean
  fri: boolean
  sat: boolean
}

export function listSchedules(token: string): Promise<Schedule[]> {
  return apiRequest<Schedule[]>('/api/schedules', { token })
}

export function getSchedule(id: string, token: string): Promise<Schedule> {
  return apiRequest<Schedule>(`/api/schedules/${id}`, { token })
}

export function createSchedule(name: string, token: string): Promise<Schedule> {
  return apiRequest<Schedule>('/api/schedules', { method: 'POST', body: { name }, token })
}

export function updateSchedule(id: string, name: string, token: string): Promise<Schedule> {
  return apiRequest<Schedule>(`/api/schedules/${id}`, { method: 'PUT', body: { name }, token })
}

export function deleteSchedule(id: string, token: string): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>(`/api/schedules/${id}`, { method: 'DELETE', token })
}

export function getScheduleSpans(id: string, token: string): Promise<{ spans: ScheduleSpan[] }> {
  return apiRequest<{ spans: ScheduleSpan[] }>(`/api/schedules/${id}/spans`, { token })
}

export function putScheduleSpans(
  id: string,
  spans: ScheduleSpanInput[],
  token: string,
): Promise<{ ok: true; spans: ScheduleSpan[] }> {
  return apiRequest<{ ok: true; spans: ScheduleSpan[] }>(`/api/schedules/${id}/spans`, {
    method: 'PUT',
    body: { spans },
    token,
  })
}

export function getCompanySchedules(
  companyId: string,
  token: string,
): Promise<{ scheduleIds: string[] }> {
  return apiRequest<{ scheduleIds: string[] }>(`/api/companies/${companyId}/schedules`, {
    token,
  })
}

export function putCompanySchedules(
  companyId: string,
  scheduleIds: string[],
  token: string,
): Promise<{ ok: true; scheduleIds: string[] }> {
  return apiRequest<{ ok: true; scheduleIds: string[] }>(`/api/companies/${companyId}/schedules`, {
    method: 'PUT',
    body: { scheduleIds },
    token,
  })
}
